import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Form, Input, Button, message } from 'antd';
import { MailOutlined, PhoneOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { getApiUrl } from '../utils/apiUrl';

const ContactUs = () => {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contactInfo, setContactInfo] = useState({
    email: '',
    phone: '',
    address: '',
    hours: '',
  });
  const [footerCopyright, setFooterCopyright] = useState('Copyright © 2026 Blue API, All rights reserved.');

  const t = (zh, en) => (lang === 'zh' ? zh : en);

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem('lang') || 'en');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 获取联系我们内容
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(getApiUrl('/api/homepage-config'));
        const data = await response.json();
        if (data.code === 0 && data.data) {
          // 解析联系方式 JSON
          if (data.data.contact_us_content) {
            try {
              const parsed = JSON.parse(data.data.contact_us_content);
              setContactInfo({
                email: parsed.email || '',
                phone: parsed.phone || '',
                address: parsed.address || '',
                hours: parsed.hours || '',
              });
            } catch (e) {
              // 如果不是 JSON 格式，保持默认
            }
          }
          if (data.data.footer_copyright) {
            setFooterCopyright(data.data.footer_copyright);
          }
        }
      } catch (error) {
        console.log('获取联系我们失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('user_token');
      const response = await fetch(getApiUrl('/api/feedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          type: 'contact',
          name: values.name,
          email: values.email,
          phone: values.phone || '',
          content: values.message,
        }),
      });
      const data = await response.json();
      if (data.code === 0) {
        message.success(t('提交成功，我们会尽快与您联系！', 'Submitted successfully, we will contact you soon!'));
        form.resetFields();
      } else {
        message.error(data.message || t('提交失败', 'Submit failed'));
      }
    } catch (error) {
      message.error(t('提交失败，请稍后重试', 'Submit failed, please try again later'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <div className="legal-nav-container">
          <Link to="/" className="legal-logo">
            <span>Blue API</span>
          </Link>
          <div className="legal-nav-links">
            <Link to="/">{t('返回首页', 'Back to Home')}</Link>
          </div>
        </div>
      </nav>

      <main className="legal-content">
        <div className="legal-container">
          {loading ? (
            <div className="legal-loading">{t('加载中...', 'Loading...')}</div>
          ) : (
            <div className="contact-grid">
              <div className="contact-info">
                <h2>{t('联系方式', 'Contact Information')}</h2>
                
                {contactInfo.email && (
                  <div className="contact-item">
                    <MailOutlined className="contact-icon" />
                    <div>
                      <h3>{t('邮箱', 'Email')}</h3>
                      <p>{contactInfo.email}</p>
                    </div>
                  </div>
                )}

                {contactInfo.phone && (
                  <div className="contact-item">
                    <PhoneOutlined className="contact-icon" />
                    <div>
                      <h3>{t('电话', 'Phone')}</h3>
                      <p>{contactInfo.phone}</p>
                    </div>
                  </div>
                )}

                {contactInfo.address && (
                  <div className="contact-item">
                    <EnvironmentOutlined className="contact-icon" />
                    <div>
                      <h3>{t('地址', 'Address')}</h3>
                      <p>{contactInfo.address}</p>
                    </div>
                  </div>
                )}

                {contactInfo.hours && (
                  <div className="contact-hours">
                    <h3>{t('工作时间', 'Working Hours')}</h3>
                    <p style={{ whiteSpace: 'pre-line' }}>{contactInfo.hours.replace(/<br\s*\/?>/gi, '\n')}</p>
                  </div>
                )}
              </div>

              <div className="contact-form-wrapper">
                <h2>{t('在线留言', 'Send Message')}</h2>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  className="contact-form"
                >
                  <Form.Item
                    name="name"
                    label={t('您的姓名', 'Your Name')}
                    rules={[{ required: true, message: t('请输入姓名', 'Please enter your name') }]}
                  >
                    <Input placeholder={t('请输入您的姓名', 'Please enter your name')} />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    label={t('邮箱地址', 'Email Address')}
                    rules={[
                      { required: true, message: t('请输入邮箱', 'Please enter your email') },
                      { type: 'email', message: t('请输入有效的邮箱地址', 'Please enter a valid email') },
                    ]}
                  >
                    <Input placeholder={t('请输入您的邮箱', 'Please enter your email')} />
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    label={t('联系电话（选填）', 'Phone Number (Optional)')}
                  >
                    <Input placeholder={t('请输入您的联系电话', 'Please enter your phone number')} />
                  </Form.Item>

                  <Form.Item
                    name="message"
                    label={t('留言内容', 'Message')}
                    rules={[{ required: true, message: t('请输入留言内容', 'Please enter your message') }]}
                  >
                    <Input.TextArea
                      rows={4}
                      placeholder={t('请输入您想咨询的内容...', 'Please enter your inquiry...')}
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={submitting}
                      block
                      size="large"
                    >
                      {t('提交留言', 'Submit Message')}
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="legal-footer">
        <p>{footerCopyright}</p>
      </footer>

      <style>{`
        .legal-page {
          min-height: 100vh;
          background: #f8fafc;
        }
        .legal-nav {
          background: #fff;
          border-bottom: 1px solid #e5e7eb;
          padding: 12px 0;
        }
        .legal-nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .legal-logo {
          font-size: 20px;
          font-weight: 600;
          color: #1677ff;
          text-decoration: none;
        }
        .legal-nav-links a {
          color: #6b7280;
          text-decoration: none;
          font-size: 14px;
        }
        .legal-nav-links a:hover {
          color: #1677ff;
        }
        .legal-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px;
          min-height: calc(100vh - 100px);
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .legal-container {
          background: #fff;
          border-radius: 12px;
          padding: 32px 40px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .legal-container h1 {
          font-size: 24px;
          font-weight: 600;
          color: #111827;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 2px solid #e5e7eb;
        }
        .contact-grid {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 48px;
          align-items: start;
        }
        .contact-info {
          padding-right: 24px;
        }
        .contact-info h2 {
          font-size: 24px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 34px;
          padding-bottom: 12px;
          border-bottom: 2px solid #e5e7eb;
        }
        .contact-form-wrapper h2 {
          font-size: 24px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 34px;
        }
        .contact-item {
          display: flex;
          align-items: flex-start;
          gap: 20px;
          margin-bottom: 34px;
          width: 100%;
        }
        .contact-icon {
          font-size: 28px;
          color: #1677ff;
          margin-top: 2px;
          flex-shrink: 0;
        }
        .contact-item h3 {
          font-size: 15px;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 6px;
        }
        .contact-item p {
          font-size: 16px;
          color: #111827;
          margin: 0;
          line-height: 1.5;
        }
        .contact-hours {
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
        }
        .contact-hours h3 {
          font-size: 15px;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 12px;
        }
        .contact-hours p {
          font-size: 15px;
          color: #4b5563;
          margin: 4px 0;
          line-height: 1.5;
        }
        .contact-form-wrapper {
          background: #f9fafb;
          padding: 28px;
          border-radius: 8px;
        }
        .contact-config-content {
          margin-bottom: 20px;
          padding-bottom: 20px;
          border-bottom: 1px solid #e5e7eb;
        }
        .contact-config-content h1 {
          margin-bottom: 16px;
          padding-bottom: 10px;
          font-size: 22px;
        }
        .contact-config-content h2 {
          font-size: 16px;
          margin-top: 16px;
          margin-bottom: 8px;
        }
        .contact-config-content p {
          font-size: 14px;
          line-height: 1.6;
          margin-bottom: 8px;
        }
        .legal-footer {
          text-align: center;
          padding: 16px;
          color: #9ca3af;
          font-size: 13px;
          border-top: 1px solid #e5e7eb;
        }
        @media (max-width: 768px) {
          .contact-grid {
            grid-template-columns: 1fr;
            gap: 32px;
          }
          .legal-container {
            padding: 24px;
          }
          .legal-container h1 {
            font-size: 24px;
          }
        }
      `}</style>
    </div>
  );
};

export default ContactUs;
